import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import type { TelegramLoginData, TelegramLoginOptions, TelegramOAuthData } from '@etap/shared';
import { getTelegramLoginOptions, loadTelegramLoginSdk } from '../lib/telegramLogin';
import styles from './TelegramLoginWidget.module.css';

interface Props {
  onAuth: (user: TelegramLoginData) => void | Promise<void>;
  botUsername: string;
  loading?: boolean;
}

/** Compatibility until the bot's new Login Widget is configured in BotFather. */
function LegacyWidget({ onAuth, botUsername, loading }: Props) {
  const { t, i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const onAuthRef = useRef(onAuth);
  onAuthRef.current = onAuth;
  const id = useId().replace(/[^a-zA-Z0-9]/g, '');
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    setFailed(false);
    const callbackName = `etapTelegramAuth${id}`;
    const callbacks = window as unknown as Record<string, unknown>;
    callbacks[callbackName] = (data: TelegramOAuthData) => { void onAuthRef.current(data); };
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '14');
    script.setAttribute('data-onauth', `${callbackName}(user)`);
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-lang', i18n.language);
    const timeout = window.setTimeout(() => setFailed(true), 12_000);
    const loaded = () => { clearTimeout(timeout); setFailed(false); };
    script.onerror = () => { clearTimeout(timeout); setFailed(true); };
    const observer = new MutationObserver(() => {
      const iframe = container.querySelector('iframe');
      if (iframe) { iframe.addEventListener('load', loaded, { once: true }); observer.disconnect(); }
    });
    observer.observe(container, { childList: true });
    // StrictMode cleans up the first effect before this task runs.
    const attach = window.setTimeout(() => container.appendChild(script), 0);
    return () => {
      clearTimeout(attach);
      clearTimeout(timeout);
      observer.disconnect();
      container.replaceChildren();
      delete callbacks[callbackName];
    };
  }, [botUsername, id, i18n.language, attempt]);

  return <div className={styles.wrapper}>
    <div ref={containerRef} className={styles.legacy} inert={loading} />
    {failed && <>
      <p className={styles.error} role="alert">{t('login.telegramUnavailable')}</p>
      <button type="button" className={styles.secondary} onClick={() => setAttempt(value => value + 1)}>{t('login.retry')}</button>
    </>}
  </div>;
}

export function TelegramLoginWidget(props: Props) {
  const { t, i18n } = useTranslation();
  const [options, setOptions] = useState<TelegramLoginOptions | null>(null);
  const [ready, setReady] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef(false);
  const activeAttempt = useRef(0);
  const onAuthRef = useRef(props.onAuth);
  onAuthRef.current = props.onAuth;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      activeAttempt.current++;
      window.Telegram?.Login?.close();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    getTelegramLoginOptions()
      // An older API deployment can still use the existing widget.
      .catch((): TelegramLoginOptions => ({ enabled: false }))
      .then(async result => {
        if (cancelled) return;
        setOptions(result);
        if (result.enabled) await loadTelegramLoginSdk();
        if (!cancelled) setReady(true);
      })
      .catch(() => { if (!cancelled) setError('login.telegramUnavailable'); });
    return () => { cancelled = true; };
  }, [attempt]);

  // A page left open for a long time should still be ready for one-click login.
  useEffect(() => {
    if (!options?.enabled || pending) return;
    const timer = window.setTimeout(() => setAttempt(value => value + 1), Math.max(0, options.expiresAt - Date.now() - 60_000));
    return () => clearTimeout(timer);
  }, [options, pending]);

  const cancel = () => {
    activeAttempt.current++;
    window.Telegram?.Login?.close();
    setPending(false);
  };

  const login = () => {
    if (!options?.enabled || !ready || pending || props.loading || !window.Telegram?.Login) return;
    if (options.expiresAt <= Date.now()) {
      setError('login.telegramExpired');
      setAttempt(value => value + 1);
      return;
    }
    setError(null);
    setPending(true);
    const currentAttempt = ++activeAttempt.current;
    // Stay synchronous with the click: awaiting a request here blocks popups on mobile Safari.
    try {
      window.Telegram.Login.auth({
        client_id: options.clientId, nonce: options.nonce,
        scope: ['profile', 'write'], lang: i18n.language,
      }, async result => {
        if (!mounted.current || activeAttempt.current !== currentAttempt) return;
        if (result.error || !result.id_token) {
          if (result.error !== 'popup_closed') setError('login.telegramFailed');
          setPending(false);
          return;
        }
        try {
          await onAuthRef.current({ id_token: result.id_token });
        } catch {
          if (mounted.current) setError('login.telegramFailed');
        } finally {
          if (mounted.current) { setPending(false); setAttempt(value => value + 1); }
        }
      });
    } catch {
      setPending(false);
      setError('login.telegramFailed');
    }
  };

  if (options && !options.enabled) return <LegacyWidget {...props} />;
  return <div className={styles.wrapper}>
    <button type="button" className={styles.button} onClick={login} disabled={!ready || pending || props.loading} aria-busy={!ready || pending || props.loading}>
      <Send size={21} aria-hidden="true" />
      {pending || props.loading ? t('login.telegramSigningIn') : !ready && !error ? t('common.loading') : t('login.telegramBtn')}
    </button>
    {pending && <>
      <p className={styles.hint} role="status">{t('login.telegramConfirm')}</p>
      <button type="button" className={styles.secondary} onClick={cancel} disabled={props.loading}>{t('login.cancel')}</button>
    </>}
    {error && <p className={styles.error} role="alert">{t(error)}</p>}
    {error && !ready && <button type="button" className={styles.secondary} onClick={() => { setError(null); setAttempt(value => value + 1); }}>{t('login.retry')}</button>}
  </div>;
}
